import { useQueryClient } from '@tanstack/react-query';

import { useCallback, useEffect, useState } from 'react';

import { Stack } from '@mui/material';

import { ContextModel, OperationExecutionTypeModel, SchemaSummaryModel } from '../../api/api';
import { useCodeStoreController } from '../CodeStore/Hooks/useCodeStoreController';
import { useEntityController } from '../EntityDesigner/Hooks/useEntityController';
import OperationList from '../OperationDesigner/Components/OperationList';
import { useOperationController } from '../OperationDesigner/Hooks/useOperationController';
import { useContextController } from './Hooks/useContextController';
import { useContextCodeArtifacts } from './Hooks/useContextCodeArtifacts';
import { ContextDesignerCodeSnapshotModel } from './Hooks/useContextDesignerSnapshot';

type ContextServiceViewProps = {
	contextId: string;
	embedded?: boolean;
	/** Incremented by parent after an application codebase advancement to trigger code-file refetch. */
	refetchTrigger?: number;
	initialContext?: ContextModel | null;
	initialCodeSnapshot?: ContextDesignerCodeSnapshotModel | null;
	initialOperationExecutionTypes?: OperationExecutionTypeModel[];
	availableEntities?: SchemaSummaryModel[];
};

export default function ContextServiceView({
	contextId,
	embedded,
	refetchTrigger,
	initialContext,
	initialCodeSnapshot,
	initialOperationExecutionTypes,
	availableEntities,
}: ContextServiceViewProps) {
	const queryClient = useQueryClient();
	const [contextData, setContextData] = useState<ContextModel | null>(null);
	const [executionTypes, setExecutionTypes] = useState<OperationExecutionTypeModel[]>([]);
	const [isWorking, setIsWorking] = useState(false);

	const {
		operations: data,
		loadingOperations: _loading,
		createOperation,
		getOperationExecutionTypes,
		refreshOperations,
		refreshSingleOperation,
		deleteOperation,
		updateOperation,
		updateOperationExposure,
		updateOperationDescription,
	} = useOperationController(contextId);

	const { addProperty, deleteProperty, entities } = useEntityController(contextId, { enabled: !availableEntities });

	const { getContextById } = useContextController();

	const { deleteCodeFile } = useCodeStoreController();
	const { data: codeArtifacts, refetch: refetchCodeArtifacts } = useContextCodeArtifacts(contextId, initialCodeSnapshot);
	const applicationCodeFiles = codeArtifacts?.applicationFiles ?? [];
	const testCodeFiles = codeArtifacts?.testFiles ?? [];
	const applicationCodeBase = codeArtifacts?.applicationCodeBase;
	const testCodeBase = codeArtifacts?.testCodeBase;

	// Fetch context data
	// Fetch functions using useCallback to preserve identity
	const fetchContextData = useCallback(async () => {
		if (!contextId) return;
		const context = await getContextById(contextId);
		setContextData(context!);
	}, [contextId, getContextById]);

	// Combined refresh function for when scopes are updated
	const handleScopesUpdated = useCallback(async () => {
		await Promise.all([fetchContextData(), refreshOperations()]);
	}, [fetchContextData, refreshOperations]);

	const fetchExecutionTypes = useCallback(async () => {
		const types = await getOperationExecutionTypes();
		setExecutionTypes(types);
	}, [getOperationExecutionTypes]);

	const fetchCodeFiles = useCallback(async () => {
		await refetchCodeArtifacts();
	}, [refetchCodeArtifacts]);

	useEffect(() => {
		const init = async () => {
			setIsWorking(true);
			try {
				if (initialContext) {
					setContextData(initialContext);
				} else {
					await fetchContextData();
				}

				if (initialOperationExecutionTypes?.length) {
					setExecutionTypes(initialOperationExecutionTypes);
				} else {
					await fetchExecutionTypes();
				}

			} finally {
				setIsWorking(false);
			}
		};
		init();
	}, [fetchContextData, fetchExecutionTypes, initialContext, initialOperationExecutionTypes]);

	// Refetch when parent signals an application codebase advancement
	useEffect(() => {
		if (refetchTrigger) {
			fetchCodeFiles();
			queryClient.invalidateQueries({ queryKey: ['codeBase', contextId, 'application'] });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refetchTrigger]);

	const handleDeleteFile = useCallback(
		async (fileName: string, codeBaseId: string) => {
			setIsWorking(true);
			try {
				await deleteCodeFile(fileName, codeBaseId);
				await fetchCodeFiles();
			} finally {
				setIsWorking(false);
			}
		},
		[deleteCodeFile, fetchCodeFiles],
	);

	const handleRefreshCodeFile = useCallback(
		async (_fileName: string) => {
			setIsWorking(true);
			try {
				await fetchCodeFiles();
				// Invalidate code base cache to get updated state
				queryClient.invalidateQueries({ queryKey: ['codeBase', contextId, 'application'] });
			} finally {
				setIsWorking(false);
			}
		},
		[fetchCodeFiles, queryClient, contextId],
	);

	return (
		<Stack direction="column" sx={{ flex: 1, minHeight: 0, overflow: 'hidden', p: 0 }}>
			<OperationList
				embedded={embedded}
				wrapMainContentInPaper={!embedded}
				onDeleteCodeFile={handleDeleteFile}
				onRefreshCodeFile={handleRefreshCodeFile}
				availableEntities={availableEntities ?? entities}
				onDeleteProperty={deleteProperty}
				onAddProperty={addProperty}
				onCreate={createOperation}
				onUpdateOperation={updateOperation}
				onUpdateExposure={updateOperationExposure}
				onUpdateDescription={updateOperationDescription}
				executionTypes={executionTypes ?? []}
				operations={data ?? []}
				applicationCodeFiles={applicationCodeFiles ?? []}
				testCodeFiles={testCodeFiles}
				applicationCodeBaseId={applicationCodeBase?.id}
				testCodeBaseId={testCodeBase?.id}
				contextId={contextId}
				availableScopes={contextData?.scopes ?? []}
				onScopesUpdated={handleScopesUpdated}
				onRefresh={refreshSingleOperation}
				onDeleteOperation={deleteOperation}
				isLoading={isWorking}
			/>
		</Stack>
	);
}
